import { Box, Tooltip } from '@mui/material';
import { memo, useMemo } from 'react';
import { CddDeleteIcon } from '../../assets/icons/Icons';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
    DataGrid,
    GridActionsCellItem,
    GridActionsCellItemProps,
    GridColDef,
    GridRowParams
} from '@mui/x-data-grid';
import CustomNoResultsOverlay from './components/CustomNoResultsOverlay';
import CustomNoRowsOverlay from './components/CustomNoRowsOverlay';

const dataGridStyles = {
    '.MuiDataGrid-iconButtonContainer': {
        visibility: 'visible !important',
        width: 'auto !important'
    },
    '.MuiDataGrid-columnHeader--sorted .MuiDataGrid-sortIcon': {
        color: '#000',
        fontSize: 'larger'
    },
    '.MuiDataGrid-columnHeaderTitleContainer': {
        justifyContent: 'space-between !important'
    },
    '.MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeader--sorted) svg': {
        opacity: '0.4 !important'
    },
    '.MuiDataGrid-columnHeader:focus, .MuiDataGrid-columnHeader:focus-within': {
        outline: 'none !important'
    },
    '.MuiDataGrid-main': {
        marginTop: 0
    },
    '.MuiDataGrid-virtualScroller': {
        '& .MuiDataGrid-row.Mui-selected': {
            backgroundColor: '#E5EEF4'
        }
    },
    '.MuiDataGrid-scrollbarFiller--header': {
        background: '#EDEDED !important'
    }
};

const dataGridContainerStyles = {
    height: '100%', // -webkit-fill-available
    width: '100%',
    '& .MuiDataGrid-root.MuiDataGrid-autoHeight': {
        height: 'inherit',
        overflow: 'auto'
    },
    '& .MuiDataGrid-main': {
        overflow: 'hidden'
    },
    '& .MuiDataGrid-columnHeader': {
        background: '#EDEDED'
    },
    '& .MuiDataGrid-columnHeaderTitle': {
        fontWeight: 800
    },
    '& .flex-center': {
        display: 'flex',
        alignItems: 'center',
        '& svg': {
            height: '34px',
            width: '34px'
        }
    },
    '& .header-center': {
        '& .MuiDataGrid-columnHeaderTitleContainer': {
            justifyContent: 'center !important'
        }
    },
    '& .MuiDataGrid-overlayWrapper': {}
};

const HandlerAction = ({
    historyAction,
    downloadAction,
    deleteAction,
    menuAction,
    ...props
}: GridActionsCellItemProps & {
    historyAction?: any;
    downloadAction?: any;
    deleteAction?: any;
    menuAction?: any;
}) => {
    return (
        <GridActionsCellItem
            {...props}
            onClick={(params: any) => {
                if (historyAction) historyAction(params);
                if (downloadAction) downloadAction(params);
                if (menuAction) menuAction(params);
                if (deleteAction) deleteAction(params);
            }}
        />
    );
};

interface DataTableProps {
    filterModelValue?: string;
    loading: boolean;
    withActions?: string[];
    headers: any;
    options: any;
    handleDownloadAction?: (params: any) => void;
    handleHistoryAction?: (params: any) => void;
    handleMenuAction?: (params: any, event: any) => void;
    handleDeleteAction?: (params: any) => void;
    customStyles?: { [key: string]: { [key: string]: string } };
    hideFooterPagination?: boolean;
    hiddenColumns?: any;
    getRowId?: boolean | string;
    onPageChange?: (params: any) => void;
    rowsPerPage?: number;
    onRowClick?: (params: any) => void;
    disableRowSelectionOnClick?: boolean;
}

function DataTable({
    handleHistoryAction,
    handleMenuAction,
    handleDownloadAction,
    handleDeleteAction,
    options,
    headers,
    withActions = [],
    loading = false,
    customStyles = undefined,
    hideFooterPagination = false,
    hiddenColumns = undefined,
    filterModelValue,
    getRowId,
    disableRowSelectionOnClick = true,
    onPageChange = undefined,
    rowsPerPage = undefined,
    onRowClick = undefined
}: DataTableProps) {
    const columns = useMemo<GridColDef<(typeof options)[number]>[]>(() => {
        if (!!withActions?.length) {
            let auxHeaders = [...headers];
            auxHeaders.push({
                field: 'actions',
                headerName: 'ACCIONES',
                type: 'actions',
                width: 110,
                resizable: false,
                getActions: (params: GridRowParams) => {
                    if (withActions.includes('download')) {
                        return [
                            <HandlerAction
                                icon={
                                    <Tooltip title="Descargar" placement="top">
                                        <DownloadOutlinedIcon
                                            sx={{ fontSize: '22px', color: 'var(--primary)' }}
                                        />
                                    </Tooltip>
                                }
                                downloadAction={() => {
                                    if (handleDownloadAction) handleDownloadAction(params);
                                }}
                                label="download"
                            />,
                            <HandlerAction
                                icon={
                                    <Tooltip title="Eliminar" placement="top">
                                        <CddDeleteIcon
                                            sx={{ fontSize: '22px', color: 'var(--primary)' }}
                                        />
                                    </Tooltip>
                                }
                                deleteAction={() => {
                                    if (handleDeleteAction) handleDeleteAction(params);
                                }}
                                label="download"
                            />,
                            <HandlerAction
                                icon={
                                    <Tooltip title="Ver historial" placement="top">
                                        <HistoryOutlinedIcon
                                            sx={{ fontSize: '22px', color: 'var(--primary)' }}
                                        />
                                    </Tooltip>
                                }
                                historyAction={() => {
                                    if (handleHistoryAction) handleHistoryAction(params);
                                }}
                                label="history"
                            />
                        ];
                    } else if (withActions.includes('menu')) {
                        return [
                            <HandlerAction
                                icon={
                                    <KeyboardArrowDownIcon
                                        sx={{ fontSize: '22px', color: 'var(--primary)' }}
                                    />
                                }
                                menuAction={(event: any) => {
                                    if (handleMenuAction) handleMenuAction(params, event);
                                }}
                                label={`menuItemBtn-${params?.id}`}
                            />
                        ];
                    } else if (withActions.includes('delete')) {
                        return [
                            <HandlerAction
                                icon={<CddDeleteIcon />}
                                deleteAction={() => {
                                    if (handleDeleteAction) handleDeleteAction(params);
                                }}
                                label="delete"
                            />
                        ];
                    } else {
                        return [
                            <HandlerAction
                                icon={
                                    <Tooltip title="Ver historial">
                                        <HistoryOutlinedIcon
                                            sx={{ fontSize: '22px', color: 'var(--primary)' }}
                                        />
                                    </Tooltip>
                                }
                                historyAction={() => {
                                    if (handleHistoryAction) handleHistoryAction(params);
                                }}
                                label="history"
                            />
                        ];
                    }
                }
            });
            return auxHeaders;
        } else {
            return headers || [];
        }
    }, [
        handleHistoryAction,
        handleDownloadAction,
        handleMenuAction,
        handleDeleteAction,
        headers,
        withActions
    ]);

    const totalRows = options?.length || 0;
    const shouldHideFooter = hideFooterPagination && totalRows <= 100;
    const effectivePageSize = shouldHideFooter ? totalRows : rowsPerPage || 10;

    return (
        <Box sx={{ ...dataGridContainerStyles }}>
            <DataGrid
                rows={options || []}
                columns={columns}
                initialState={{
                    columns: {
                        columnVisibilityModel: {
                            ...(hiddenColumns && hiddenColumns)
                        }
                    },
                    pagination: {
                        paginationModel: {
                            pageSize: effectivePageSize
                        }
                    }
                }}
                pageSizeOptions={[5, 10, 20, 50, 100]}
                localeText={{ columnHeaderSortIconLabel: 'Ordenar' }}
                sx={{ ...dataGridStyles, ...(customStyles && { ...customStyles }) }}
                slotProps={{
                    pagination: {
                        labelRowsPerPage: 'Elementos por página',
                        labelDisplayedRows: (page: any) =>
                            `${page.from}-${page.to} de ${page.count} items`
                    },
                    toolbar: { showQuickFilter: true }
                }}
                filterModel={{
                    items: [],
                    quickFilterExcludeHiddenColumns: false,
                    quickFilterValues: filterModelValue?.trimEnd()?.split(' ')
                }}
                disableColumnMenu={true}
                slots={{
                    noRowsOverlay: () => <CustomNoRowsOverlay />,
                    noResultsOverlay: () => <CustomNoResultsOverlay />
                }}
                disableRowSelectionOnClick={disableRowSelectionOnClick}
                hideFooter={shouldHideFooter}
                hideFooterSelectedRowCount
                columnHeaderHeight={52}
                rowHeight={52}
                onRowClick={onRowClick && onRowClick}
                loading={loading}
                onPaginationModelChange={(data: any) => onPageChange && onPageChange(data)}
                getRowId={(row: any) => (getRowId ? row?.idDocumento : row?.id)}
                hideFooterPagination={shouldHideFooter}
            />
        </Box>
    );
}

export default memo(DataTable);
